/**
 * The page the person was heading to when the auth machine sent them to sign
 * in. The machine keeps it in its context, but signing in changes the
 * identity, and `Rehydrated` (graphql.tsx) unmounts everything below it — the
 * auth provider included — while it restores that identity's cache. The
 * provider that mounts afterwards starts a new machine, which would land on
 * `/login` and from there on the home page. Kept per tab (sessionStorage), the
 * new machine starts with it.
 *
 * It also covers signing in with Google: the app leaves for the hosted UI and
 * comes back to the origin in the same tab, where Amplify keeps its own
 * OAuth state in sessionStorage too.
 */

const STORAGE_KEY = 'bilderit:auth-destination';

// A path of this app: not another site, not `//host` and not `/\host`.
export const isAppPath = (value: unknown): value is string =>
  typeof value === 'string' && /^\/(?![/\\])/.test(value);

export const rememberAuthDestination = (path: string) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, path);
  } catch {
    // Without storage the person lands on the home page after signing in.
  }
};

export const recallAuthDestination = (): string | undefined => {
  try {
    const path = sessionStorage.getItem(STORAGE_KEY);
    return isAppPath(path) ? path : undefined;
  } catch {
    return undefined;
  }
};

export const forgetAuthDestination = () => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing stored.
  }
};
